import { Overlay } from "@fluentui/react/lib/Overlay"
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner"
import { IProcessedStyleSet } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React, { ForwardedRef, useEffect } from "react"

import { BusyOverlayStyles } from "./BusyOverlay.styles"
import {
    BusyOverlayHandle,
    BusyOverlayProps,
    IBusyOverlayStyles
} from "./BusyOverlay.types"

function preventInput(event: Event) {
    event.preventDefault()
    event.stopPropagation()
}

const LISTENER_TYPES = ["keydown", "keypress", "keyup"]

/**
 * The BusyOverlay component renders modal loading spinner over the configurator (excluding the visualization).
 *
 * Each interaction with the configuration triggers the configuration service and blocks input to CUI with
 * this BusyOverlay.
 *
 * Links:
 * - [Checkout the code](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/BusyOverlay/BusyOverlay.tsx)
 * - [BusyOverlayStyles](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/BusyOverlay/BusyOverlay.styles.ts)
 * - [BusyOverlayProps](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/BusyOverlay/BusyOverlay.types.ts)
 * - [MS Fluent Spinner](https://developer.microsoft.com/de-DE/fluentui#/controls/web/spinner)
 *
 * @visibleName BusyOverlay
 */
const IBusyOverlay: React.FunctionComponent<BusyOverlayProps> =
    React.forwardRef(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (props: BusyOverlayProps, _ref: ForwardedRef<BusyOverlayHandle>) => {
            const { styles, theme } = props
            const classNames = classNamesFunction()(
                styles,
                theme
            ) as IProcessedStyleSet<IBusyOverlayStyles>
            const [hidden, setHidden] = React.useState(props.initiallyHidden)
            React.useImperativeHandle(props.componentRef, () => ({
                setVisible: (b) => setHidden(!b),
                isVisible: () => hidden === true
            }))

            useEffect(() => {
                const cuiRoot = document.getElementById("cui")
                if (cuiRoot) {
                    if (hidden) {
                        LISTENER_TYPES.forEach((type) =>
                            cuiRoot.removeEventListener(type, preventInput)
                        )
                    } else {
                        LISTENER_TYPES.forEach((type) =>
                            cuiRoot.addEventListener(type, preventInput)
                        )
                        return () => {
                            LISTENER_TYPES.forEach((type) =>
                                cuiRoot.removeEventListener(type, preventInput)
                            )
                        }
                    }
                }
            }, [props])

            if (hidden) {
                return null
            }
            return (
                <Overlay className={classNames.root}>
                    <Spinner
                        size={SpinnerSize.large}
                        className={classNames.spinner}
                    />
                </Overlay>
            )
        }
    )

export const BusyOverlay = styled(IBusyOverlay, BusyOverlayStyles)
